import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Phone, PhoneOff, Sparkles, Users, Loader2, Skull, RefreshCw, Check, X, Dice5 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type GamePhase = 'landing' | 'setup' | 'playing' | 'challenge' | 'calling' | 'result' | 'dare' | 'end';

interface Player {
  id: string;
  name: string;
  score: number;
  dares: number;
}

interface Challenge {
  type: 'word' | 'phrase' | 'scenario';
  content: string;
  bonus?: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

interface Dare {
  content: string;
  source: 'ai' | 'players';
}

const fallbackChallenges: Challenge[] = [
  { type: 'word', content: 'Placer le mot "BANANE" dans la conversation', difficulty: 'easy' },
  { type: 'word', content: 'Dire "Mon hamster fait de la musique" de façon naturelle', difficulty: 'medium' },
  { type: 'phrase', content: 'Demander si leur frigo fait des bruits bizarres la nuit', difficulty: 'medium' },
  { type: 'scenario', content: 'Faire croire que tu appelles du service après-vente des nuages', difficulty: 'hard' },
];

const fallbackDares: string[] = [
  "Fais 10 pompes devant tout le monde",
  "Imite un animal pendant 30 secondes",
  "Raconte ton moment le plus gênant",
  "Laisse les autres poster un message sur tes réseaux",
  "Bois un mélange préparé par les autres joueurs",
];

export default function AppelAbsurde() {
  const [phase, setPhase] = useState<GamePhase>('landing');
  const [players, setPlayers] = useState<Player[]>([]);
  const [playerName, setPlayerName] = useState('');
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [currentChallenge, setCurrentChallenge] = useState<Challenge | null>(null);
  const [aiDare, setAiDare] = useState<string>('');
  const [playerDare, setPlayerDare] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingDare, setIsGeneratingDare] = useState(false);
  const [round, setRound] = useState(1);
  const [maxRounds] = useState(3);
  const { toast } = useToast();

  const currentPlayer = players[currentPlayerIndex];

  const addPlayer = () => {
    if (playerName.trim() && players.length < 8) {
      setPlayers([...players, { id: crypto.randomUUID(), name: playerName.trim(), score: 0, dares: 0 }]);
      setPlayerName('');
    }
  };

  const removePlayer = (id: string) => {
    setPlayers(players.filter(p => p.id !== id));
  };

  const generateChallenge = async () => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-call-challenge', {
        body: { round, playerName: currentPlayer?.name }
      });

      if (error) throw error;

      if (data && data.content) {
        setCurrentChallenge(data);
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      console.error('Error generating challenge:', error);
      const fallback = fallbackChallenges[Math.floor(Math.random() * fallbackChallenges.length)];
      setCurrentChallenge(fallback);
      toast({
        title: "Mode hors-ligne",
        description: "Défi classique utilisé",
        variant: "default"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const generateAiDare = async () => {
    setIsGeneratingDare(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-dare', {
        body: { playerName: currentPlayer?.name, context: 'call_refused' }
      });

      if (error) throw error;

      if (data && data.dare) {
        setAiDare(data.dare);
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      console.error('Error generating dare:', error);
      const fallback = fallbackDares[Math.floor(Math.random() * fallbackDares.length)];
      setAiDare(fallback);
    } finally {
      setIsGeneratingDare(false);
    }
  };

  const startGame = async () => {
    setPhase('playing');
    setCurrentPlayerIndex(0);
    setRound(1);
  };

  const startTurn = async () => {
    await generateChallenge();
    setPhase('challenge');
  };

  const acceptChallenge = () => {
    setPhase('calling');
  };

  const refuseChallenge = async () => {
    await generateAiDare();
    setPlayerDare('');
    setPhase('dare');
  };

  const handleCallSuccess = () => {
    setPlayers(players.map((p, i) => 
      i === currentPlayerIndex ? { ...p, score: p.score + (currentChallenge?.difficulty === 'hard' ? 3 : currentChallenge?.difficulty === 'medium' ? 2 : 1) } : p
    ));
    setPhase('result');
  };

  const handleCallFail = async () => {
    await generateAiDare();
    setPlayerDare('');
    setPhase('dare');
  };

  const completeDare = (source: 'ai' | 'players') => {
    setPlayers(players.map((p, i) => 
      i === currentPlayerIndex ? { ...p, dares: p.dares + 1 } : p
    ));
    setPhase('result');
  };

  const nextTurn = () => {
    if (currentPlayerIndex < players.length - 1) {
      setCurrentPlayerIndex(prev => prev + 1);
      setPhase('playing');
    } else if (round < maxRounds) {
      setRound(prev => prev + 1);
      setCurrentPlayerIndex(0);
      setPhase('playing');
    } else {
      setPhase('end');
    }
  };

  const resetGame = () => {
    setPhase('landing');
    setPlayers([]);
    setCurrentPlayerIndex(0);
    setRound(1);
    setCurrentChallenge(null);
    setAiDare('');
    setPlayerDare('');
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-400 bg-green-500/20 border-green-500/30';
      case 'medium': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
      case 'hard': return 'text-red-400 bg-red-500/20 border-red-500/30';
      default: return 'text-green-400 bg-green-500/20 border-green-500/30';
    }
  };

  const getDifficultyLabel = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'Facile (+1pt)';
      case 'medium': return 'Moyen (+2pts)';
      case 'hard': return 'Difficile (+3pts)';
      default: return 'Facile';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-950 via-slate-900 to-slate-950 text-white relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-600/5 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 p-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Retour</span>
        </Link>
        <div className="flex items-center gap-2">
          <Phone className="w-6 h-6 text-emerald-400" />
          <span className="font-bold text-emerald-400">Appel Absurde</span>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 container mx-auto px-4 py-8 max-w-2xl">
        
        {/* Landing */}
        {phase === 'landing' && (
          <div className="text-center space-y-8 animate-fade-in">
            <div className="space-y-4">
              <Phone className="w-24 h-24 mx-auto text-emerald-400 animate-pulse" />
              <h1 className="text-5xl font-black tracking-tight bg-gradient-to-r from-emerald-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">
                APPEL ABSURDE
              </h1>
              <p className="text-slate-400 text-lg max-w-md mx-auto">
                Appelle des gens au hasard et place les phrases absurdes de l'IA. Sinon... GAGE !
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto text-left">
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                <Phone className="w-8 h-8 text-emerald-400 mb-2" />
                <h3 className="font-bold text-emerald-300">Défis IA</h3>
                <p className="text-sm text-slate-400">Mots & phrases à placer</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                <Skull className="w-8 h-8 text-emerald-400 mb-2" />
                <h3 className="font-bold text-emerald-300">Gages</h3>
                <p className="text-sm text-slate-400">IA ou joueurs, tu choisis</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                <Dice5 className="w-8 h-8 text-emerald-400 mb-2" />
                <h3 className="font-bold text-emerald-300">Aléatoire</h3>
                <p className="text-sm text-slate-400">Appelle qui tu veux</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                <Users className="w-8 h-8 text-emerald-400 mb-2" />
                <h3 className="font-bold text-emerald-300">Convivial</h3>
                <p className="text-sm text-slate-400">Fous rires garantis</p>
              </div>
            </div>

            <Button 
              onClick={() => setPhase('setup')}
              className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-bold px-8 py-6 text-lg rounded-xl shadow-lg shadow-emerald-500/25"
            >
              <Phone className="w-5 h-5 mr-2" />
              Commencer
            </Button>
          </div>
        )}

        {/* Setup */}
        {phase === 'setup' && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center space-y-2">
              <Users className="w-12 h-12 mx-auto text-emerald-400" />
              <h2 className="text-3xl font-bold">Les Appelants</h2>
              <p className="text-slate-400">2 à 8 joueurs pour le chaos</p>
            </div>

            <div className="flex gap-2">
              <Input
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addPlayer()}
                placeholder="Nom du joueur..."
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                maxLength={20}
              />
              <Button 
                onClick={addPlayer} 
                disabled={!playerName.trim() || players.length >= 8}
                className="bg-emerald-500 hover:bg-emerald-600 text-black"
              >
                Ajouter
              </Button>
            </div>

            <div className="space-y-2">
              {players.map((player, index) => (
                <div 
                  key={player.id}
                  className="flex items-center justify-between bg-slate-800/50 px-4 py-3 rounded-lg border border-slate-700/50"
                >
                  <span className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-400 font-bold text-sm">
                      {index + 1}
                    </span>
                    {player.name}
                  </span>
                  <button 
                    onClick={() => removePlayer(player.id)}
                    className="text-slate-500 hover:text-red-400 transition-colors"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {players.length < 2 && (
              <p className="text-center text-slate-500 text-sm">
                Minimum 2 joueurs ({2 - players.length} restant{2 - players.length > 1 ? 's' : ''})
              </p>
            )}

            <Button 
              onClick={startGame}
              disabled={players.length < 2}
              className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-bold py-6 text-lg rounded-xl"
            >
              <Phone className="w-5 h-5 mr-2" />
              C'est parti !
            </Button>
          </div>
        )}

        {/* Playing - Player's turn */}
        {phase === 'playing' && currentPlayer && (
          <div className="space-y-6 animate-fade-in text-center">
            <div className="space-y-2">
              <p className="text-slate-400">Manche {round}/{maxRounds}</p>
              <h2 className="text-3xl font-bold text-emerald-400">C'est au tour de...</h2>
            </div>

            <div className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 p-8 rounded-2xl border border-emerald-500/30">
              <p className="text-5xl font-black text-white">{currentPlayer.name}</p>
              <p className="text-slate-400 mt-2">Score : {currentPlayer.score} pts</p>
            </div>

            <Button 
              onClick={startTurn}
              disabled={isGenerating}
              className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-bold py-6 text-lg rounded-xl"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  L'IA prépare ton défi...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Voir mon défi
                </>
              )}
            </Button>
          </div>
        )}

        {/* Challenge */}
        {phase === 'challenge' && currentPlayer && currentChallenge && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center space-y-2">
              <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-sm">
                <Sparkles className="w-4 h-4" />
                Défi généré par IA
              </div>
              <h2 className="text-3xl font-bold">{currentPlayer.name}</h2>
            </div>

            <div className="bg-slate-800/50 p-6 rounded-xl border border-emerald-500/30 space-y-4">
              <div className={`inline-block px-3 py-1 rounded-full text-sm border ${getDifficultyColor(currentChallenge.difficulty)}`}>
                {getDifficultyLabel(currentChallenge.difficulty)}
              </div>
              
              <p className="text-xl text-white font-medium">
                📞 {currentChallenge.content}
              </p>
              
              {currentChallenge.bonus && (
                <p className="text-emerald-400 text-sm">
                  💎 Bonus : {currentChallenge.bonus}
                </p>
              )}
            </div>

            <div className="bg-cyan-500/10 p-4 rounded-xl border border-cyan-500/30">
              <p className="text-cyan-400 text-sm">
                💡 Appelle n'importe qui de ton répertoire et place ce défi dans la conversation !
              </p>
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={refuseChallenge}
                variant="outline"
                className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/20"
              >
                <X className="w-4 h-4 mr-2" />
                Je refuse (gage)
              </Button>
              <Button 
                onClick={acceptChallenge}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-bold"
              >
                <Phone className="w-4 h-4 mr-2" />
                J'appelle !
              </Button>
            </div>

            <Button 
              onClick={generateChallenge}
              disabled={isGenerating}
              variant="ghost"
              className="w-full text-slate-400 hover:text-white"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
              Autre défi
            </Button>
          </div>
        )}

        {/* Calling */}
        {phase === 'calling' && currentPlayer && currentChallenge && (
          <div className="space-y-6 animate-fade-in text-center">
            <div className="space-y-2">
              <Phone className="w-16 h-16 mx-auto text-emerald-400 animate-pulse" />
              <h2 className="text-3xl font-bold">Appel en cours...</h2>
              <p className="text-slate-400">{currentPlayer.name} doit placer le défi</p>
            </div>

            <div className="bg-slate-800/50 p-6 rounded-xl border border-emerald-500/30">
              <p className="text-lg text-white font-medium">
                📞 {currentChallenge.content}
              </p>
            </div>

            <p className="text-slate-500 text-sm">
              Les autres joueurs écoutent et jugent !
            </p>

            <div className="flex gap-3">
              <Button 
                onClick={handleCallFail}
                variant="outline"
                className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/20 py-6"
              >
                <PhoneOff className="w-5 h-5 mr-2" />
                Raté / Raccroché
              </Button>
              <Button 
                onClick={handleCallSuccess}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-bold py-6"
              >
                <Check className="w-5 h-5 mr-2" />
                Réussi !
              </Button>
            </div>
          </div>
        )}

        {/* Dare */}
        {phase === 'dare' && currentPlayer && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center space-y-2">
              <Skull className="w-16 h-16 mx-auto text-red-400" />
              <h2 className="text-3xl font-bold text-red-400">GAGE !</h2>
              <p className="text-slate-400">{currentPlayer.name}, choisis ton poison</p>
            </div>

            <div className="space-y-4">
              {/* AI Dare */}
              <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 p-5 rounded-xl border border-purple-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  <span className="font-bold text-purple-400">Gage IA</span>
                </div>
                {isGeneratingDare ? (
                  <div className="flex items-center gap-3 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    L'IA prépare ton gage...
                  </div>
                ) : (
                  <>
                    <p className="text-white text-lg mb-4">{aiDare}</p>
                    <Button 
                      onClick={() => completeDare('ai')}
                      className="w-full bg-purple-500 hover:bg-purple-600 text-white"
                    >
                      Je fais ce gage
                    </Button>
                  </>
                )}
              </div>

              <div className="text-center text-slate-500 font-bold">OU</div>

              {/* Player Dare */}
              <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 p-5 rounded-xl border border-orange-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5 text-orange-400" />
                  <span className="font-bold text-orange-400">Gage des joueurs</span>
                </div>
                <p className="text-slate-400 text-sm mb-3">Les autres joueurs décident d'un gage :</p>
                <Input
                  value={playerDare}
                  onChange={(e) => setPlayerDare(e.target.value)}
                  placeholder="Entrez le gage décidé par les joueurs..."
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 mb-3"
                />
                <Button 
                  onClick={() => completeDare('players')}
                  disabled={!playerDare.trim()}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                >
                  Je fais ce gage
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Result */}
        {phase === 'result' && currentPlayer && (
          <div className="space-y-6 animate-fade-in text-center">
            <div className="space-y-2">
              <div className="text-6xl">🎉</div>
              <h2 className="text-3xl font-bold">Tour terminé !</h2>
            </div>

            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700/50">
              <p className="text-2xl font-bold text-emerald-400">{currentPlayer.name}</p>
              <p className="text-slate-400">Score : {currentPlayer.score} pts</p>
              <p className="text-slate-500 text-sm">Gages subis : {currentPlayer.dares}</p>
            </div>

            <Button 
              onClick={nextTurn}
              className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-bold py-6 text-lg rounded-xl"
            >
              {currentPlayerIndex < players.length - 1 || round < maxRounds ? 'Joueur suivant' : 'Voir les résultats'}
            </Button>
          </div>
        )}

        {/* End */}
        {phase === 'end' && (
          <div className="space-y-6 animate-fade-in text-center">
            <div className="space-y-2">
              <div className="text-6xl">🏆</div>
              <h2 className="text-3xl font-bold">Partie terminée !</h2>
            </div>

            <div className="space-y-3">
              {[...players].sort((a, b) => b.score - a.score).map((player, index) => (
                <div 
                  key={player.id}
                  className={`p-4 rounded-xl border ${
                    index === 0 
                      ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500/30' 
                      : 'bg-slate-800/50 border-slate-700/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : ''}
                      </span>
                      <span className={`font-bold ${index === 0 ? 'text-yellow-400' : 'text-white'}`}>
                        {player.name}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-400">{player.score} pts</p>
                      <p className="text-xs text-slate-500">{player.dares} gages</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                onClick={resetGame}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Nouvelle partie
              </Button>
              <Link to="/" className="flex-1">
                <Button className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-bold">
                  Retour à l'accueil
                </Button>
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
